import { Box, Flex, forwardRef, Popover, StyleProps } from '@chakra-ui/react';
import { Children, PropsWithChildren, ReactElement } from 'react';

import { CalendarOutline } from '@bonusly/ds-icon';

import { useDateRangePicker } from '../../hooks/use-date-range-picker';
import { DateRange } from '../../types';
import { Calendar } from '../calendar/calendar';
import { CalendarControls } from '../calendar/calendar-controls';
import { CalendarFooter } from '../calendar/calendar-footer';
import { CalendarRangeHeader } from '../calendar/calendar-range-header';
import { DatePickerContext } from '../context';
import { DateRangeInput } from '../date-range-picker/date-range-input';
import { DateRangePopup } from '../date-range-picker/date-range-popup';

export type DateRangeSelectorProps = PropsWithChildren<
	StyleProps & {
		displayFormat?: 'short' | 'long';
		isClearable?: boolean;
		leftIcon?: ReactElement;
		onChange: (value: DateRange) => void;
		value: DateRange;
	}
>;

export const DateRangeSelector = forwardRef<DateRangeSelectorProps, 'button'>(
	({ children, displayFormat = 'short', isClearable = true, leftIcon = <CalendarOutline />, onChange, value, ...rest }, ref) => {
		const picker = useDateRangePicker({ onChange, value });

		return (
			<DatePickerContext value={{ displayFormat, isClearable, value, ...picker }}>
				<Popover isLazy isOpen={picker.isOpen} onClose={picker.onClose}>
					<DateRangeInput leftIcon={leftIcon} ref={ref} {...rest} />

					<DateRangePopup bg="bg.canvas" borderColor="transparent" p={1} w="unset">
						<Flex columnGap={2}>
							<Box flex={1}>
								<Box
									as="ul"
									listStyleType="none"
									sx={{
										'li:not(:last-child)': {
											mb: 1,
										},
									}}
								>
									{Children.map(Children.toArray(children), (child) => (
										<Box as="li">{child}</Box>
									))}
								</Box>
							</Box>
							<Box bg="white" borderRadius="sm" boxShadow="sm" p={3}>
								<CalendarControls mb={1} />
								<CalendarRangeHeader mb={1} />
								<Calendar mb={2} />
								<CalendarFooter />
							</Box>
						</Flex>
					</DateRangePopup>
				</Popover>
			</DatePickerContext>
		);
	}
);

DateRangeSelector.displayName = 'DateRangeSelector';
